import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { Observable, of, timer, forkJoin } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { backendEndPointToken, patientBackendEndPointToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { DisableCountParam, DisableSortParam, LoadingUIEnabled } from '../loading.interceptor';
import { Bundle, Patient } from 'fhir/r4';

/**
 * Provides async validator factories that query the FHIR server to detect
 * duplicate patient registrations by email or NIN (National Identification Number).
 *
 * Usage:
 *   // In your component:
 *   customValidators = this.dupCheck.buildValidatorRegistry('org-id-here');
 *
 * The registry includes the organization tenant `_tag` so the search is scoped
 * to the current organization — critical for self-registration flows where no
 * user is logged in and the interceptor cannot inject the tag automatically.
 */
@Injectable({ providedIn: 'root' })
export class PatientDuplicateCheckService {
  private http = inject(HttpClient);
  private fhirBase = inject(backendEndPointToken);
  private unAuthBackend = inject(patientBackendEndPointToken)
  private terminologyBase = inject(terminologyCanonicalUrlBaseToken);

  /** Context that suppresses the loading spinner and FHIR count/sort rewrites. */
  private silentCtx = new HttpContext()
    .set(LoadingUIEnabled, false)
    .set(DisableCountParam, true)
    .set(DisableSortParam, true);

  /** Build the `_tag` value the interceptor uses for tenant scoping. */
  private tenantTagValue(orgId: string): string {
    return `${this.terminologyBase}/CodeSystem/organizationTenant|${orgId}`;
  }

  /**
   * Async validator that checks whether a patient with the given email already
   * exists in the FHIR server, scoped to the specified organization.
   *
   * FHIR search: GET /Patient?email=<email>&_tag=<tenantSystem>|<orgId>&_count=1&_total=accurate
   *
   * Returns { duplicateEmail: true } when a match is found, null otherwise.
   */
  emailExistsValidator(organizationId: string, debounceMs = 600): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const email = (control.value ?? '').toString().trim();
      if (!email) return of(null);

      let patientUrl = `${this.unAuthBackend}/Patient?email=${encodeURIComponent(email)}&active=true&_count=1&_total=accurate`;
      let practitionerUrl = `${this.fhirBase}/Practitioner?email=${encodeURIComponent(email)}&active=true&_count=1&_total=accurate`;

      if (organizationId) {
        const tagQuery = `&_tag=${encodeURIComponent(this.tenantTagValue(organizationId))}`;
        patientUrl += tagQuery;
        practitionerUrl += tagQuery;
      }

      return timer(debounceMs).pipe(
        switchMap(() => {
          const patientReq = this.http.get<Bundle<any>>(patientUrl, { context: this.silentCtx }).pipe(catchError(() => of(null)));
          const practitionerReq = this.http.get<Bundle<any>>(practitionerUrl, { context: this.silentCtx }).pipe(catchError(() => of(null)));

          return forkJoin({ patient: patientReq, practitioner: practitionerReq }).pipe(
            map((results) => {
              const patientTotal = results.patient?.total ?? (results.patient?.entry?.length ?? 0);
              const practitionerTotal = results.practitioner?.total ?? (results.practitioner?.entry?.length ?? 0);

              return (patientTotal > 0 || practitionerTotal > 0) ? { duplicateEmail: true } : null;
            })
          );
        })
      );
    };
  }

  /**
   * Async validator that checks whether a patient with the given NIN already
   * exists in the FHIR server, scoped to the specified organization.
   *
   * FHIR search: GET /Patient?identifier=<nin>&_tag=<tenantSystem>|<orgId>&_count=1&_total=accurate
   *
   * Returns { duplicateNin: true } when a match is found, null otherwise.
   */
  ninExistsValidator(organizationId: string, debounceMs = 600): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const nin = (control.value ?? '').toString().trim();
      if (!nin) return of(null); // NIN is optional — only check if provided

      let url = `${this.unAuthBackend}/Patient?identifier=${encodeURIComponent(nin)}&active=true&_count=1&_total=accurate`;
      if (organizationId) {
        url += `&_tag=${encodeURIComponent(this.tenantTagValue(organizationId))}`;
      }

      return timer(debounceMs).pipe(
        switchMap(() =>
          this.http
            .get<Bundle<Patient>>(url, { context: this.silentCtx })
            .pipe(
              map((bundle) => {
                const total = bundle?.total ?? (bundle?.entry?.length ?? 0);
                return total > 0 ? { duplicateNin: true } : null;
              }),
              catchError(() => of(null))
            )
        )
      );
    };
  }

  /**
   * Returns a registry object keyed by validator name, ready to pass to
   * <app-dynamic-forms-v2 [customValidators]="...">
   *
   * @param organizationId  The org to scope duplicate checks to.
   *                         When empty, queries are unscoped (all orgs).
   */
  buildValidatorRegistry(organizationId: string): Record<string, AsyncValidatorFn> {
    return {
      emailExists: this.emailExistsValidator(organizationId),
      ninExists: this.ninExistsValidator(organizationId),
    };
  }
}
